import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faWindowMinimize, faExternalLinkAlt, faImage } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import ChatMessageItem from './ChatMessageItem';
import ChatInput from './ChatInput';
import type { ChatInputHandle } from './ChatInput';
import type {
  DayTradingSession,
  DayTradingSessionChat,
} from '@/models/day-trading-session';
import type { DayTradingSessionID, UserID } from '@/models/ids';

interface ChatPanelProps {
  chat: DayTradingSessionChat;
  sessionId: DayTradingSessionID;
  currentUserId: UserID;
  canWrite: boolean;
  canDelete: boolean;
  isMuted: boolean;
  onChatUpdate: (chat: DayTradingSessionChat) => void;
  onMinimize?: () => void;
  onOpenStandalone?: () => void;
  standalone?: boolean;
}

export default function ChatPanel({
  chat,
  sessionId,
  currentUserId,
  canWrite,
  canDelete,
  isMuted,
  onChatUpdate,
  onMinimize,
  onOpenStandalone,
  standalone = false,
}: ChatPanelProps) {
  const { play } = useNotificationSound(isMuted);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevLatestIdRef = useRef<string | null>(null);
  const [windowFocused, setWindowFocused] = useState<boolean>(
    typeof document !== 'undefined' ? document.hasFocus() : true,
  );
  const [flashUnread, setFlashUnread] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const chatInputRef = useRef<ChatInputHandle>(null);

  const messages = chat.messages;
  const latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const lastViewedId = chat.lastViewed[currentUserId]?.messageID ?? null;

  const hasUnread =
    latestMessage !== null &&
    latestMessage.id !== lastViewedId &&
    latestMessage.userID !== currentUserId;

  // Track window focus
  useEffect(() => {
    const onFocus = () => setWindowFocused(true);
    const onBlur = () => setWindowFocused(false);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  const handleSessionUpdate = useCallback(
    (session: DayTradingSession) => {
      onChatUpdate(session.chat);
    },
    [onChatUpdate],
  );

  // Mark latest message as viewed on the server.
  const markViewedInFlightRef = useRef<string | null>(null);
  const markLatestViewed = useCallback(async () => {
    if (latestMessage === null) return;
    if (latestMessage.id === lastViewedId) return;
    if (markViewedInFlightRef.current === latestMessage.id) return;
    markViewedInFlightRef.current = latestMessage.id;
    try {
      const session = await APIRoutes.markDayTradingChatMessageViewed(sessionId, latestMessage.id);
      onChatUpdate(session.chat);
    } catch {
      // non-fatal
    } finally {
      if (markViewedInFlightRef.current === latestMessage.id) {
        markViewedInFlightRef.current = null;
      }
    }
  }, [latestMessage, lastViewedId, sessionId, onChatUpdate]);

  // Auto-scroll to bottom: on initial mount (incl. restore from collapsed),
  // and whenever a new message arrives. We also follow content resizes
  // (e.g. images finishing loading) while we are stuck at the bottom.
  const stickToBottomRef = useRef(true);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el === null) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useLayoutEffect(() => {
    stickToBottomRef.current = true;
    scrollToBottom();
    // Re-scroll after paint in case layout sizes change (images, fonts).
    const raf = requestAnimationFrame(() => scrollToBottom());
    return () => cancelAnimationFrame(raf);
  }, [messages.length, latestMessage?.id, scrollToBottom]);

  // Observe the scroll content; while stuck to bottom, keep it pinned as
  // children grow (image load, layout shifts).
  useEffect(() => {
    const el = scrollRef.current;
    if (el === null) return;

    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
      stickToBottomRef.current = atBottom;
    };
    el.addEventListener('scroll', onScroll, { passive: true });

    const ro = new ResizeObserver(() => {
      if (stickToBottomRef.current) scrollToBottom();
    });
    for (const child of Array.from(el.children)) {
      ro.observe(child);
    }
    // Also listen for late image loads.
    const imgs = el.querySelectorAll('img');
    const onImgLoad = () => {
      if (stickToBottomRef.current) scrollToBottom();
    };
    imgs.forEach((img) => img.addEventListener('load', onImgLoad));

    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
      imgs.forEach((img) => img.removeEventListener('load', onImgLoad));
    };
  }, [messages.length, scrollToBottom]);

  // Handle new-message effects: sound + mark-viewed + flash animation
  useEffect(() => {
    if (latestMessage === null) {
      return;
    }
    const prevId = prevLatestIdRef.current;
    prevLatestIdRef.current = latestMessage.id;

    // First render: don't play sound; just mark viewed if window focused
    if (prevId === null) {
      if (windowFocused && hasUnread) markLatestViewed();
      return;
    }

    if (prevId === latestMessage.id) {
      // Not a new message — maybe reaction/edit update. Still mark viewed
      // opportunistically if we're focused.
      if (windowFocused && hasUnread) markLatestViewed();
      return;
    }

    // New incoming message
    const isMine = latestMessage.userID === currentUserId;
    if (!isMine) {
      play('message');
    }

    if (windowFocused) {
      markLatestViewed();
    } else {
      setFlashUnread(true);
    }
  }, [latestMessage, windowFocused, hasUnread, currentUserId, play, markLatestViewed]);

  // When the window regains focus, mark viewed and stop flashing.
  useEffect(() => {
    if (windowFocused) {
      setFlashUnread(false);
      if (hasUnread) markLatestViewed();
    }
  }, [windowFocused, hasUnread, markLatestViewed]);

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!canWrite) return;
    const types = e.dataTransfer?.types;
    if (types === undefined || !Array.from(types).includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragging(true);
  }, [canWrite]);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!canWrite) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  }, [canWrite]);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!canWrite) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer !== null) e.dataTransfer.dropEffect = 'copy';
  }, [canWrite]);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!canWrite) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    const handle = chatInputRef.current;
    if (handle === null || handle.isBusy()) return;
    const files = e.dataTransfer?.files;
    if (files === undefined || files.length === 0) return;
    for (const file of files) {
      if (file.type.startsWith('image/')) {
        handle.setImage(file);
        return;
      }
    }
  }, [canWrite]);

  // Any user interaction inside the panel marks latest as read (failsafe).
  const handleInteraction = useCallback(() => {
    if (hasUnread) markLatestViewed();
  }, [hasUnread, markLatestViewed]);

  return (
    <div
      onClick={handleInteraction}
      onScroll={handleInteraction}
      onKeyDown={handleInteraction}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`relative bg-white dark:bg-gray-800 rounded-xl border shadow-sm overflow-hidden flex flex-col ${
        flashUnread
          ? 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-400/50 animate-pulse'
          : 'border-gray-200 dark:border-gray-700'
      } ${standalone ? 'h-full' : 'max-w-[360px] w-full'}`}
      style={standalone ? undefined : { height: '32rem' }}
    >
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faComments} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="font-semibold text-gray-900 dark:text-white text-sm">Chat</span>
        </div>
        <div className="flex items-center gap-2">
          {onOpenStandalone !== undefined && !standalone && (
            <button
              onClick={onOpenStandalone}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer transition-colors"
              title="In neuem Fenster öffnen"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3.5 h-3.5" />
            </button>
          )}
          {onMinimize !== undefined && !standalone && (
            <button
              onClick={onMinimize}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer transition-colors"
              title="Minimieren"
            >
              <FontAwesomeIcon icon={faWindowMinimize} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto min-h-0"
      >
        {messages.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
            Noch keine Nachrichten.
          </div>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {messages.map((m) => (
              <ChatMessageItem
                key={m.id}
                message={m}
                sessionId={sessionId}
                currentUserId={currentUserId}
                canReact={canWrite}
                canDelete={canDelete}
                onSessionUpdate={handleSessionUpdate}
              />
            ))}
          </div>
        )}
      </div>

      {canWrite && (
        <ChatInput ref={chatInputRef} sessionId={sessionId} onSessionUpdate={handleSessionUpdate} />
      )}

      {isDragging && canWrite && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-blue-500/15 dark:bg-blue-400/15 pointer-events-none rounded-xl border-2 border-dashed border-blue-500 dark:border-blue-400">
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/95 dark:bg-gray-900/95 text-sm font-medium text-blue-700 dark:text-blue-300 shadow">
            <FontAwesomeIcon icon={faImage} className="w-4 h-4" />
            Bild hier ablegen
          </div>
        </div>
      )}
    </div>
  );
}
